<script setup lang="ts">
import { computed } from 'vue';
import { useADialog } from '@Atoms';

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

const duplicatedPersons = computed(() => props.rowData?.duplicated_persons || []);

const hasTargetKey = computed(() => {
  if (!props.settings?.render) return false;

  const { targetKey } = props.settings.render;

  return targetKey.length > 1 ? !!props.rowData[targetKey[0]][targetKey[1]] : !!props.rowData[targetKey[0]];
});
const targetRef = computed(() => {
  if (!props.settings?.render) return null;

  const { href, targetKey, secondaryKey } = props.settings.render;

  if (secondaryKey) {
    const primary = props.rowData[targetKey[0]];
    const secondary = props.rowData[secondaryKey[0]];

    if (primary != null && secondary != null) return `${href}${primary}/${secondary}`;

    return null;
  }

  if (targetKey.length > 1) {
    const primary = props.rowData[targetKey[0]];
    if (primary && typeof primary === 'object' && primary[targetKey[1]] != null)
      return `${href}${primary[targetKey[1]]}`;

    return null;
  }

  if (props.rowData[targetKey[0]] != null) return `${href}${props.rowData[targetKey[0]]}`;

  return null;
});

const hasDoNotAdoptOrFoster = computed(() =>
  duplicatedPersons.value.some((person: any) => person.do_not_adopt || person.do_not_foster)
);

const hasDoNotAdoptRowData = computed(() => !!props.rowData?.do_not_adopt);

function onOpenDuplicatedPersonsModal() {
  activeModalProps.value = {
    rowData: props.rowData,
  };
  activeModal.value = SYSTEM_MODALS.DUPLICATED_PERSONS_MODAL;
  dialogVisibility.value = true;
}
</script>

<template>
  <div class="flex items-center gap-2">
    <RouterLink
      v-if="!field && settings?.canBeUnknown && targetRef"
      class="af-link overflow-hidden"
      :to="targetRef"
    >
      {{ $t('common.unknown') }}
    </RouterLink>

    <span
      v-else-if="!field"
      class="flex flex-col items-start gap-0.5"
    >
      {{ settings?.composedEmptyState ? $t(settings.composedEmptyState) : '-' }}
    </span>

    <a
      v-else-if="settings?.render?.type === 'mail'"
      class="af-link overflow-hidden hover:underline"
      target="_blank"
      :href="'mailto:' + field"
    >
      {{ field }}
    </a>

    <RouterLink
      v-else-if="targetRef"
      class="af-link group flex flex-col gap-0 overflow-hidden text-ellipsis"
      :to="targetRef"
    >
      <span class="overflow-hidden text-ellipsis group-hover:underline">
        {{ settings.hasName ? field.name : field }}
      </span>

      <span
        v-if="hasDoNotAdoptRowData"
        class="flex w-fit items-center gap-1 overflow-hidden text-ellipsis rounded-md bg-red-500 px-1 py-0 text-center text-[10px] font-semibold text-white"
      >
        <i class="pi pi-ban text-[10px] text-white" />

        {{ hasDoNotAdoptRowData ? $t('people.individualPage.profileInfo.actionsOptions.doNotAdopt') : '' }}
      </span>
    </RouterLink>

    <p
      v-else-if="!hasTargetKey"
      class="overflow-hidden text-ellipsis"
    >
      {{ field }}
    </p>

    <p
      v-else
      class="overflow-hidden text-ellipsis"
    >
      {{ field || '-' }}
    </p>

    <div
      class="flex cursor-pointer items-center gap-1 rounded-md p-1 transition-colors hover:bg-gray-100"
      @click="onOpenDuplicatedPersonsModal"
    >
      <i
        v-if="duplicatedPersons.length"
        v-tooltip.top="{
          value: $t('formsAndApplications.tooltip.duplicatedPerson'),
          showDelay: 1000,
        }"
        class="pi pi-exclamation-triangle text-lg text-warning-500"
      />

      <i
        v-if="duplicatedPersons.length && hasDoNotAdoptOrFoster"
        v-tooltip.top="$t('people.individualPage.profileInfo.actionsOptions.doNotTooltip')"
        class="pi pi-ban text-red-500"
      />
    </div>
  </div>
</template>
